import type { Course, Lesson } from "../api/course-service";
import type { UserProgress } from "../api/user-progress-service";

import { fDate } from "./formatTime";
import { isRecapDue, LESSON_MARK_LABEL, lessonProgress } from "./course-progress";

/**
 * How a lesson looks on the metro map:
 * - `done` — finished;
 * - `recap` — started and its recap is due today;
 * - `waiting` — started, the recap comes later;
 * - `current` — the one lesson suggested next;
 * - `open` — not started, its prerequisites are verified;
 * - `locked` — not started, a prerequisite is not verified yet; it can still be opened;
 * - `off` — too few questions to be verified.
 */
/** What a locked stop says when the missing prerequisite is on its own line, in sight. */
export const LOCKED_LABEL = 'prerequisites first';

export type StopKind = 'done' | 'recap' | 'waiting' | 'current' | 'open' | 'locked' | 'off';

export type MetroStop = {
    lesson: Lesson;
    kind: StopKind;
    /** Low-importance lessons are bonus spurs, off the line the course is finished along. */
    bonus: boolean;
    /** The line under the title, e.g. "recap due" or "needs Generics". */
    caption?: string;
    /** The full state, read by screen readers. */
    label: string;
};

export function isBonus(lesson: Lesson): boolean {
    return lesson.importance === 'low';
}

/** Whether the line through a lesson counts as travelled: at least its first session is passed. */
export function isVerified(lesson: Lesson, progress: UserProgress | null): boolean {
    return lessonProgress(progress, lesson).status !== 'NOT_STARTED';
}

/** A prerequisite nobody can verify yet does not hold the lessons after it back. */
function isSatisfied(lesson: Lesson | undefined, progress: UserProgress | null): boolean {
    return !lesson || !lesson.practicable || isVerified(lesson, progress);
}

function missingPrerequisites(lesson: Lesson, byId: Map<string, Lesson>, progress: UserProgress | null): Lesson[] {
    return lesson.previous
        .map((id) => byId.get(id))
        .filter((previous): previous is Lesson => !!previous && !isSatisfied(previous, progress));
}

/** The first lesson of the plan the user can start now, preferring the required ones over bonus spurs. */
export function nextLesson(course: Course, progress: UserProgress | null): Lesson | undefined {
    const byId = new Map(course.lessons.map((lesson) => [ lesson.id, lesson ]));
    const startable = course.lessons.filter((lesson) => lesson.practicable
        && lessonProgress(progress, lesson).status === 'NOT_STARTED'
        && !missingPrerequisites(lesson, byId, progress).length);

    return startable.find((lesson) => !isBonus(lesson)) ?? startable[0];
}

export function metroStops(course: Course, progress: UserProgress | null): MetroStop[] {
    const byId = new Map(course.lessons.map((lesson) => [ lesson.id, lesson ]));
    const next = nextLesson(course, progress);

    return course.lessons.map((lesson) => {
        const kind = stopKind(lesson, next, byId, progress);
        const caption = stopCaption(lesson, kind, byId, progress);
        const status = lesson.practicable ? LESSON_MARK_LABEL[lessonProgress(progress, lesson).status] : LESSON_MARK_LABEL.DISABLED;

        const state = caption ?? (kind === 'locked' ? LOCKED_LABEL : status);

        return { lesson, kind, bonus: isBonus(lesson), caption, label: `${ lesson.title } — ${ state }` };
    });
}

function stopKind(lesson: Lesson, next: Lesson | undefined, byId: Map<string, Lesson>, progress: UserProgress | null): StopKind {
    if (!lesson.practicable) return 'off';

    const { status, recapDate } = lessonProgress(progress, lesson);
    if (status === 'FINISHED') return 'done';
    if (status === 'STARTED') return isRecapDue(recapDate) ? 'recap' : 'waiting';
    if (lesson.id === next?.id) return 'current';

    return missingPrerequisites(lesson, byId, progress).length ? 'locked' : 'open';
}

function stopCaption(lesson: Lesson, kind: StopKind, byId: Map<string, Lesson>, progress: UserProgress | null): string | undefined {
    if (kind === 'off') return LESSON_MARK_LABEL.DISABLED;
    if (kind === 'recap') return LESSON_MARK_LABEL.STARTED;
    if (kind === 'waiting') return `recap on ${ fDate(lessonProgress(progress, lesson).recapDate ?? undefined) }`;
    if (kind === 'current') return 'up next';

    // Prerequisites of the same group are visible as the line itself; only the ones elsewhere need words
    const elsewhere = missingPrerequisites(lesson, byId, progress).filter((previous) => previous.groupId !== lesson.groupId);
    if (kind === 'locked' && elsewhere.length) return `needs ${ elsewhere.slice(0, 2).map((previous) => previous.title).join(' + ') }`;
    if (isBonus(lesson)) return kind === 'locked' ? 'bonus · locked' : 'bonus';

    return undefined;
}
